import { AppVersion } from 'click-types';
import { PixelRatio, Platform, StyleSheet } from 'react-native';
import { pick } from 'lodash';
import { useSelector } from 'react-redux';

import { appInfoSelector } from '@common/redux/selectors/app';
import { checkFont, getShadow } from '../func';

const createStyles = (attributes: any) => {
  const appInfo = useSelector(appInfoSelector);
  const isV4 = appInfo?.version === AppVersion.V4;

  let effectiveAttrs = attributes;

  if (isV4) {
    // Prioritize styles from attributes.style (where brand typography is applied)
    effectiveAttrs = { ...attributes, ...attributes.style };
  }

  const getDecoration = () => {
    return {};
  };

  const getTextDecorationLine = () => {
    // Use the new textDecorationLine if provided, otherwise fall back to old logic
    if (effectiveAttrs.textDecorationLine) {
      return effectiveAttrs.textDecorationLine;
    }

    const through = 'line-through';
    const line = 'underline';
    const combine = 'underline line-through';

    if (
      effectiveAttrs.textDecorationLineThrough &&
      effectiveAttrs.textDecorationUnderline
    ) {
      return combine;
    }
    if (effectiveAttrs.textDecorationLineThrough) {
      return through;
    }
    if (effectiveAttrs.textDecorationUnderline) {
      return line;
    }
    return 'none';
  };

  const checkLineHeight = () => {
    if (
      !effectiveAttrs.lineHeight ||
      effectiveAttrs.lineHeight < effectiveAttrs.fontSize * 1.15
    ) {
      return Math.ceil(effectiveAttrs.fontSize * 1.15);
    }
    return +effectiveAttrs.lineHeight;
  };

  const checkColor = () => {
    if (effectiveAttrs.color === '@text') {
      return 'black';
    }
    return effectiveAttrs.color;
  };

  const getFontWeight = () => {
    // Use the new fontWeight if provided
    if (appInfo?.version === AppVersion.V4 && effectiveAttrs.fontWeight) {
      return effectiveAttrs.fontWeight?.toString();
    }

    // Fall back to old logic
    return effectiveAttrs.fontWeightBold === true &&
      effectiveAttrs.fontFamily === 'Roboto' &&
      Platform.OS === 'android'
      ? 'normal'
      : effectiveAttrs.fontWeightBold
      ? 'bold'
      : 'normal';
  };

  const getFontStyle = () => {
    // Use the new fontStyle if provided
    if (appInfo?.version === AppVersion.V4 && effectiveAttrs.fontStyle) {
      return effectiveAttrs.fontStyle;
    }

    // Fall back to old logic
    return effectiveAttrs.fontStyleItalic ? 'italic' : 'normal';
  };

  const getFontFamily = () => {
    // Use the new fontFamily if provided
    if (appInfo?.version === AppVersion.V4 && effectiveAttrs.fontFamily) {
      return checkFont(effectiveAttrs.fontFamily, effectiveAttrs.fontWeight);
    }

    // Fall back to old logic
    return !effectiveAttrs.fontStyleItalic
      ? checkFont(effectiveAttrs.fontFamily, effectiveAttrs.fontWeightBold)
      : undefined;
  };

  const styles = {
    container: {
      ...pick(effectiveAttrs, [
        'marginTop',
        'marginLeft',
        'width',
        'zIndex',
        'backgroundColor',
        'borderRadius',
        'maxWidth',
        'position',
      ]),
      ...(isV4
        ? pick(effectiveAttrs, [
            'left',
            'top',
            'right',
            'bottom',
            'position',
            'transform',
            'margin',
            'marginTop',
            'marginLeft',
            'marginRight',
            'marginBottom',
          ])
        : {}),
      // Handle width: 'auto' for React Native - use flex: 1 instead
      ...(Platform.OS !== 'web' && effectiveAttrs.width === 'auto'
        ? { flex: 1, width: undefined }
        : {}),
      ...(!isV4 ? { minHeight: effectiveAttrs.height } : {}),
      padding: (effectiveAttrs?.borderRadius || 0) / 2,
      ...getShadow(effectiveAttrs),
      ...(effectiveAttrs.borderStyle !== 'none'
        ? pick(effectiveAttrs, ['borderWidth', 'borderColor'])
        : {}),
      borderStyle:
        effectiveAttrs.borderStyle === 'none'
          ? 'solid'
          : effectiveAttrs.borderStyle,
    },
    wrapper: {},
    text: {
      ...pick(effectiveAttrs, ['zIndex', 'letterSpacing']),
      ...getDecoration(),
      // TODO deprecate textAlignment
      textAlign: effectiveAttrs.textAlign || effectiveAttrs.textAlignment,
      fontStyle: getFontStyle(),
      fontFamily: getFontFamily(),
      fontSize: effectiveAttrs.fontSize / PixelRatio.getFontScale(),
      fontWeight: getFontWeight(),
      textDecorationLine: getTextDecorationLine(),
      ...(() => {
        // Handle height properties based on different conditions
        if (effectiveAttrs?.isInCustomList) {
          return { minHeight: effectiveAttrs.height };
        }

        if (effectiveAttrs.multiline) {
          return null; // No height constraint for multiline
        }
      })(),
      lineHeight:
        checkLineHeight() / PixelRatio.getFontScale() -
        (effectiveAttrs.borderWidth || 0) * 2,
      color: checkColor(),
    },
    icon: {
      position: 'absolute' as const,
      right: 10,
    },
  };

  // For React Native use StyleSheet.create, for web use plain objects
  return Platform.OS === 'web' ? styles : StyleSheet.create(styles);
};

export default createStyles;
